/**
 * Motor de preço — regras `v1-legado` (padrão) e `v2` (desligada até aprovação).
 *
 * A aritmética usa decimal.js para não acumular erro de ponto flutuante;
 * só o preço final é arredondado (2 casas, meio para cima).
 *
 * Referências: docs/contexto/13-motor-de-preco.md (fórmula e gabarito),
 * 14-base-legal-icms.md (artigos) e 17-spec-fase-1.md §4 (contrato).
 */
import Decimal from "decimal.js";
import { ErroPreco } from "./erros";
import type {
  CondicaoPagamento,
  EntradaPreco,
  EntradaPrecoV1,
  EntradaPrecoV2,
  EntradaPrecoV3,
  IcmsManual,
  ParametrosMarkup,
  PassoCalculo,
  ResultadoPreco,
  Viabilidade,
} from "./tipos";

/** Decimal isolado do global, com precisão folgada e arredondamento meio-para-cima. */
const D = Decimal.clone({ precision: 40, rounding: Decimal.ROUND_HALF_UP });
type Dec = InstanceType<typeof D>;

const FONTE_DIFAL_SIMPLES =
  "LC 123/2006, art. 13, § 1º, XIII, \"h\"; RICMS-SE (Dec. 21.400/2002), art. 674-A";

/** Rótulo do custo na memória de cálculo, pela condição de pagamento (D111). */
function descricaoCusto(pagamento: CondicaoPagamento | undefined): string {
  return pagamento === "prazo" ? "Custo unitário a prazo (sem o desconto à vista)" : "Custo unitário à vista";
}

function exigirFinito(campo: string, valor: number): void {
  if (!Number.isFinite(valor)) {
    throw new ErroPreco(campo, `O campo ${campo} precisa ser um número.`);
  }
}

function exigirFracao(campo: string, valor: number): void {
  exigirFinito(campo, valor);
  if (valor < 0 || valor >= 1) {
    throw new ErroPreco(campo, `O campo ${campo} precisa estar entre 0 e 1 (fração), recebido ${valor}.`);
  }
}

function exigirUf(campo: string, uf: string): void {
  if (!/^[A-Z]{2}$/.test(uf)) {
    throw new ErroPreco(campo, `UF inválida em ${campo}: "${uf}".`);
  }
}

function validarComum(entrada: EntradaPreco): void {
  exigirFinito("custo_avista", entrada.custo_avista);
  if (entrada.custo_avista <= 0) {
    throw new ErroPreco("custo_avista", "O custo à vista precisa ser maior que zero.");
  }
  exigirUf("uf_origem", entrada.uf_origem);
  exigirUf("uf_destino", entrada.uf_destino);
  const m = entrada.markup;
  exigirFracao("margem", m.margem);
  exigirFracao("seguro", m.seguro);
  exigirFracao("custo_fixo", m.custo_fixo);
  exigirFracao("simples", m.simples);
  if (entrada.icms_manual) {
    validarIcmsManual(entrada.icms_manual);
  }
  if (entrada.valor_unit_edital !== undefined && entrada.valor_unit_edital !== null) {
    exigirFinito("valor_unit_edital", entrada.valor_unit_edital);
  }
}

function validarIcmsManual(manual: IcmsManual): void {
  exigirFinito("icms_manual.aliquota", manual.aliquota);
  if (manual.aliquota <= -1 || manual.aliquota >= 1) {
    throw new ErroPreco("icms_manual.aliquota", "O ICMS manual precisa ser uma fração entre −1 e 1.");
  }
  if (manual.justificativa.trim().length === 0) {
    throw new ErroPreco("icms_manual.justificativa", "O ICMS digitado à mão exige justificativa.");
  }
}

/**
 * Markup por divisor: 1 / (1 − Σ percentuais). Aba MARK-UP da planilha.
 * Devolve também o divisor (1 − Σ): o preço é calculado como base ÷ divisor,
 * para que um valor exato como 0,025 não vire 0,02499… e arredonde para baixo.
 */
function calcularMarkup(m: ParametrosMarkup, fretePct: Dec, passos: PassoCalculo[]): { markup: Dec; divisor: Dec } {
  const soma = new D(m.margem).plus(m.seguro).plus(m.custo_fixo).plus(m.simples).plus(fretePct);
  passos.push({
    chave: "soma_divisor",
    descricao: "Soma dos percentuais do divisor",
    formula: "margem + seguro + custo_fixo + simples + frete_pct",
    valor: soma.toString(),
  });
  if (soma.gte(1)) {
    throw new ErroPreco("markup", `A soma dos percentuais do divisor é ${soma.times(100).toString()}%: precisa ser menor que 100%.`);
  }
  const divisor = new D(1).minus(soma);
  const markup = new D(1).div(divisor);
  passos.push({
    chave: "markup",
    descricao: "Markup por divisor",
    formula: "1 / (1 − soma_divisor)",
    valor: markup.toString(),
  });
  return { markup, divisor };
}

function viabilidade(preco: Dec, edital: number | null | undefined): Viabilidade {
  if (edital === undefined || edital === null) return "sem_edital";
  const cmp = preco.cmp(edital);
  if (cmp < 0) return "viavel";
  if (cmp > 0) return "inviavel";
  return "igual";
}

function montarResultado(
  entrada: EntradaPreco,
  passos: PassoCalculo[],
  base: Dec,
  icms: Dec,
  icmsOrigem: "calculado" | "manual",
  markup: { markup: Dec; divisor: Dec },
): ResultadoPreco {
  const exato = base.div(markup.divisor);
  const arredondado = exato.toDecimalPlaces(2, D.ROUND_HALF_UP);
  passos.push({
    chave: "preco_unit_exato",
    descricao: "Preço unitário sem arredondamento",
    formula: "base_com_icms × markup (calculado como base_com_icms ÷ (1 − soma_divisor))",
    valor: exato.toString(),
  });
  passos.push({
    chave: "preco_unit",
    descricao: "Preço unitário (2 casas, meio para cima)",
    formula: "arredondar(preco_unit_exato, 2)",
    valor: arredondado.toFixed(2),
  });
  const v = viabilidade(arredondado, entrada.valor_unit_edital);
  return {
    regra: entrada.regra,
    preco_unit: arredondado.toNumber(),
    preco_unit_exato: exato.toString(),
    markup: markup.markup.toNumber(),
    icms_aplicado: icms.toNumber(),
    icms_origem: icmsOrigem,
    viabilidade: v,
    memoria_calculo: { regra: entrada.regra, entrada, passos },
  };
}

/**
 * Regra v1-legado — reproduz a planilha exatamente, inclusive os erros conhecidos
 * (ICMS negativo em compra dentro do estado; ST ignorado; frete fora da base).
 *
 *   icms  = (interna_destino − interestadual) − (reducao_informatica se informática = "S")
 *   preco = custo_avista × (1 + icms) × markup
 */
function calcularV1(entrada: EntradaPrecoV1): ResultadoPreco {
  validarComum(entrada);
  exigirFracao("frete_pct", entrada.frete_pct);
  exigirFracao("aliquotas.interna_destino", entrada.aliquotas.interna_destino);
  exigirFracao("aliquotas.interestadual", entrada.aliquotas.interestadual);
  exigirFracao("reducao_informatica", entrada.reducao_informatica);

  const passos: PassoCalculo[] = [];
  const custo = new D(entrada.custo_avista);
  passos.push({ chave: "custo_avista", descricao: descricaoCusto(entrada.pagamento), formula: "entrada", valor: custo.toString() });

  let icms: Dec;
  let origem: "calculado" | "manual";
  if (entrada.icms_manual) {
    icms = new D(entrada.icms_manual.aliquota);
    origem = "manual";
    passos.push({
      chave: "icms",
      descricao: `ICMS digitado à mão — justificativa: ${entrada.icms_manual.justificativa}`,
      formula: "entrada manual",
      valor: icms.toString(),
    });
  } else {
    origem = "calculado";
    if (entrada.informatica === null) {
      // Na planilha, com INFORMÁTICA vazia o ICMS fica em branco e o preço não sai.
      throw new ErroPreco(
        "informatica",
        "A marcação de informática (S/N) está vazia. Informe S ou N, ou digite o ICMS à mão com justificativa.",
      );
    }
    const diferenca = new D(entrada.aliquotas.interna_destino).minus(entrada.aliquotas.interestadual);
    passos.push({
      chave: "difal",
      descricao: `Diferencial de alíquota ${entrada.uf_origem} → ${entrada.uf_destino}`,
      formula: "interna_destino − interestadual",
      valor: diferenca.toString(),
      fonte: FONTE_DIFAL_SIMPLES,
    });
    if (entrada.informatica === "S") {
      icms = diferenca.minus(entrada.reducao_informatica);
      passos.push({
        chave: "icms",
        descricao: "ICMS com redução de informática",
        formula: "difal − reducao_informatica",
        valor: icms.toString(),
        fonte: "RICMS-SE, art. 40, I (19%) e IX (12%, Anexo III); art. 40-D (+1 p.p. FECOEP; art. 40-B revogado em 01/05/2023)",
      });
    } else {
      icms = diferenca;
      passos.push({ chave: "icms", descricao: "ICMS sem redução", formula: "difal", valor: icms.toString() });
    }
  }

  const base = custo.times(new D(1).plus(icms));
  passos.push({
    chave: "base_com_icms",
    descricao: "Custo acrescido do ICMS (custo, não entra no divisor)",
    formula: "custo_avista × (1 + icms)",
    valor: base.toString(),
    fonte: "RICMS-SE, art. 674-A, § 4º (não gera crédito)",
  });

  const markup = calcularMarkup(entrada.markup, new D(entrada.frete_pct), passos);
  return montarResultado(entrada, passos, base, icms, origem, markup);
}


/** Frete do produto (compra, D96): soma ao custo. Usado pela v2 e pela v3. */
function freteDaCompra(entrada: EntradaPrecoV2 | EntradaPrecoV3, custo: Dec, passos: PassoCalculo[]): Dec {
  let freteValor = new D(0);
  switch (entrada.frete_compra.tipo) {
    case "incluso":
      passos.push({ chave: "frete_compra", descricao: "Frete do produto incluso no preço do fornecedor (CIF)", formula: "0", valor: "0" });
      break;
    case "fob_valor":
      exigirFinito("frete_compra.valor_unit", entrada.frete_compra.valor_unit);
      if (entrada.frete_compra.valor_unit < 0) {
        throw new ErroPreco("frete_compra.valor_unit", "O frete do produto por unidade não pode ser negativo.");
      }
      freteValor = new D(entrada.frete_compra.valor_unit);
      passos.push({ chave: "frete_compra", descricao: "Frete do produto FOB por unidade (soma ao custo)", formula: "entrada", valor: freteValor.toString() });
      break;
    case "fob_estimado_pct":
      exigirFracao("frete_compra.pct", entrada.frete_compra.pct);
      freteValor = custo.times(entrada.frete_compra.pct);
      passos.push({
        chave: "frete_compra",
        descricao: "Frete do produto FOB estimado em percentual do custo (soma ao custo)",
        formula: "custo_avista × frete_compra_pct",
        valor: freteValor.toString(),
      });
      break;
  }

  return freteValor;
}

/** Frete de envio ao órgão (D96): percentual no divisor do markup. Usado pela v2 e pela v3. */
function freteDoEnvio(entrada: EntradaPrecoV2 | EntradaPrecoV3, passos: PassoCalculo[]): Dec {
  let fretePct = new D(0);
  if (entrada.frete_envio.tipo === "percentual") {
    exigirFracao("frete_envio.pct", entrada.frete_envio.pct);
    fretePct = new D(entrada.frete_envio.pct);
    passos.push({ chave: "frete_envio", descricao: "Frete de envio ao órgão em percentual (no divisor)", formula: "entrada", valor: fretePct.toString() });
  } else {
    passos.push({ chave: "frete_envio", descricao: "Sem frete de envio (escolha explícita)", formula: "0", valor: "0" });
  }
  return fretePct;
}

/**
 * Regra v2 — correções de `13` (itens 1–3, 11 e 12) e `14`. Desligada até aprovação.
 *
 *   icms       = 0 se a compra é dentro do estado ou com ST incluso;
 *                senão max(0, interna_aplicavel − interestadual_nf)
 *   custo_base = custo_avista + frete_compra + ipi        (frete do produto, D96)
 *   markup     = 1 / (1 − (margem + seguro + custo_fixo + simples + frete_envio))
 *   preco      = (custo_base + custo_base × icms) × markup
 */
function calcularV2(entrada: EntradaPrecoV2): ResultadoPreco {
  validarComum(entrada);
  exigirFracao("interna_destino", entrada.interna_destino);
  exigirFracao("interna_destino_informatica", entrada.interna_destino_informatica);
  exigirFinito("ipi_valor_unit", entrada.ipi_valor_unit);
  if (entrada.ipi_valor_unit < 0) {
    throw new ErroPreco("ipi_valor_unit", "O IPI por unidade não pode ser negativo.");
  }

  const passos: PassoCalculo[] = [];
  const custo = new D(entrada.custo_avista);
  passos.push({ chave: "custo_avista", descricao: descricaoCusto(entrada.pagamento), formula: "entrada", valor: custo.toString() });

  const freteValor = freteDaCompra(entrada, custo, passos);
  const fretePct = freteDoEnvio(entrada, passos);

  const ipi = new D(entrada.ipi_valor_unit);
  const custoBase = custo.plus(freteValor).plus(ipi);
  passos.push({
    chave: "custo_base",
    descricao: "Custo com frete do produto e IPI (base da complementação)",
    formula: "custo_avista + frete_compra + ipi",
    valor: custoBase.toString(),
    fonte: "RICMS-SE, art. 674-A, § 1º (base inclui IPI, frete e demais despesas)",
  });

  let icms: Dec;
  let origem: "calculado" | "manual";
  if (entrada.icms_manual) {
    icms = new D(entrada.icms_manual.aliquota);
    origem = "manual";
    passos.push({
      chave: "icms",
      descricao: `ICMS digitado à mão — justificativa: ${entrada.icms_manual.justificativa}`,
      formula: "entrada manual",
      valor: icms.toString(),
    });
  } else if (entrada.uf_origem === entrada.uf_destino) {
    icms = new D(0);
    origem = "calculado";
    passos.push({
      chave: "icms",
      descricao: "Compra dentro do estado: sem complementação",
      formula: "0",
      valor: "0",
      fonte: "LC 123/2006, art. 13, § 1º, XIII, \"h\" (aquisições em outros Estados); RICMS-SE, art. 674-A, § 3º",
    });
  } else if (entrada.st_incluso) {
    icms = new D(0);
    origem = "calculado";
    passos.push({
      chave: "icms",
      descricao: "ST incluso no custo: sem complementação",
      formula: "0",
      valor: "0",
      fonte: "Intenção da planilha (coluna ST); base legal a confirmar com o contador",
    });
  } else {
    if (entrada.interestadual_nf === null) {
      throw new ErroPreco(
        "interestadual_nf",
        "Compra de outro estado: informe a alíquota interestadual da NF ou da cotação (4%, 7% ou 12%).",
      );
    }
    exigirFracao("interestadual_nf", entrada.interestadual_nf);
    const interna = entrada.informatica_anexo_iii ? entrada.interna_destino_informatica : entrada.interna_destino;
    passos.push({
      chave: "interna_aplicavel",
      descricao: entrada.informatica_anexo_iii ? "Alíquota interna de informática (Anexo III)" : "Alíquota interna geral",
      formula: "entrada",
      valor: new D(interna).toString(),
      fonte: entrada.informatica_anexo_iii
        ? "RICMS-SE, art. 40, IX + art. 40-D"
        : "RICMS-SE, art. 40, I + art. 40-D; Lei SE 3.796/1996, art. 18, I, \"j\"",
    });
    const diferenca = new D(interna).minus(entrada.interestadual_nf);
    icms = D.max(0, diferenca);
    passos.push({
      chave: "icms",
      descricao: "Complementação de alíquota (mínimo zero)",
      formula: "max(0, interna_aplicavel − interestadual_nf)",
      valor: icms.toString(),
      fonte: `${FONTE_DIFAL_SIMPLES}; RSF 13/2012 (4% importado)`,
    });
    origem = "calculado";
  }

  const base = custoBase.times(new D(1).plus(icms));
  passos.push({
    chave: "base_com_icms",
    descricao: "Custo base acrescido do ICMS",
    formula: "custo_base × (1 + icms)",
    valor: base.toString(),
    fonte: "RICMS-SE, art. 674-A, § 4º (não gera crédito)",
  });

  const markup = calcularMarkup(entrada.markup, fretePct, passos);
  return montarResultado(entrada, passos, base, icms, origem, markup);
}

/**
 * Regra v3 (D107) — fórmula da complementação como a SEFAZ-SE calcula no DIA (validada com 19 NFs reais, doc `21` §4):
 *
 *   base_entrada = custo_avista × (1 − reducao_base)                 (base do ICMS na NF de compra)
 *   base_674     = base_entrada + frete_compra + ipi                  (RICMS-SE, art. 674-A, § 1º)
 *   icms_valor   = max(0, base_674 × interna_sem_fecoep − base_entrada × interestadual)   (§ 2º)
 *   fecoep_valor = base_674 × fecoep, salvo material escolar          (§ 8º; art. 616-C-A, IV, "c")
 *   preco        = (custo_avista + frete_compra + ipi + icms_valor + fecoep_valor) × markup
 *
 * Compra dentro do estado ou com ST incluso: sem complementação e sem FECOEP (Lei SE 4.731/2002, art. 2º-B, II:
 * o adicional do Simples só é devido junto com a complementação).
 */
function calcularV3(entrada: EntradaPrecoV3): ResultadoPreco {
  validarComum(entrada);
  exigirFracao("interna_destino", entrada.interna_destino);
  exigirFracao("interna_destino_informatica", entrada.interna_destino_informatica);
  exigirFracao("fecoep", entrada.fecoep);
  exigirFracao("reducao_base", entrada.reducao_base);
  exigirFinito("ipi_valor_unit", entrada.ipi_valor_unit);
  if (entrada.ipi_valor_unit < 0) {
    throw new ErroPreco("ipi_valor_unit", "O IPI por unidade não pode ser negativo.");
  }

  const passos: PassoCalculo[] = [];
  const custo = new D(entrada.custo_avista);
  passos.push({ chave: "custo_avista", descricao: descricaoCusto(entrada.pagamento), formula: "entrada", valor: custo.toString() });
  const freteValor = freteDaCompra(entrada, custo, passos);
  const fretePct = freteDoEnvio(entrada, passos);
  const ipi = new D(entrada.ipi_valor_unit);
  const custoBase = custo.plus(freteValor).plus(ipi);
  passos.push({
    chave: "custo_base",
    descricao: "Custo com frete do produto e IPI",
    formula: "custo_avista + frete_compra + ipi",
    valor: custoBase.toString(),
  });

  let impostoValor: Dec;
  let origem: "calculado" | "manual";
  if (entrada.icms_manual) {
    origem = "manual";
    impostoValor = custoBase.times(entrada.icms_manual.aliquota);
    passos.push({
      chave: "icms",
      descricao: `ICMS digitado à mão (fração sobre o custo base) — justificativa: ${entrada.icms_manual.justificativa}`,
      formula: "custo_base × icms_manual",
      valor: impostoValor.toString(),
    });
  } else if (entrada.uf_origem === entrada.uf_destino) {
    origem = "calculado";
    impostoValor = new D(0);
    passos.push({
      chave: "icms",
      descricao: "Compra dentro do estado: sem complementação e sem FECOEP",
      formula: "0",
      valor: "0",
      fonte: "LC 123/2006, art. 13, § 1º, XIII, \"h\"; RICMS-SE, art. 674-A, § 3º; Lei SE 4.731/2002, art. 2º-B, II",
    });
  } else if (entrada.st_incluso) {
    origem = "calculado";
    impostoValor = new D(0);
    passos.push({
      chave: "icms",
      descricao: "ST incluso no custo: sem complementação (PENDENTE: confirmar com o contador, doc 21)",
      formula: "0",
      valor: "0",
      fonte: "LC 123/2006, art. 13, § 1º, XIII, \"g\", 1 (encerramento da tributação)",
    });
  } else {
    if (entrada.interestadual_nf === null) {
      throw new ErroPreco(
        "interestadual_nf",
        "Compra de outro estado: informe a alíquota interestadual legal da operação (4%, 7% ou 12%).",
      );
    }
    exigirFracao("interestadual_nf", entrada.interestadual_nf);
    origem = "calculado";
    const fecoep = new D(entrada.fecoep);
    const internaComFecoep = new D(entrada.informatica_anexo_iii ? entrada.interna_destino_informatica : entrada.interna_destino);
    const interna = internaComFecoep.minus(fecoep);
    if (interna.lt(0)) throw new ErroPreco("fecoep", "O FECOEP é maior que a alíquota interna.");
    passos.push({
      chave: "interna_aplicavel",
      descricao: entrada.informatica_anexo_iii ? "Alíquota interna de informática do Anexo III, sem o FECOEP" : "Alíquota interna geral, sem o FECOEP",
      formula: "interna_com_fecoep − fecoep",
      valor: interna.toString(),
      fonte: entrada.informatica_anexo_iii
        ? "RICMS-SE, art. 40, IX (12%); Lei SE 3.796/1996, art. 18, I, \"i\""
        : "RICMS-SE, art. 40, I (19%); Lei SE 3.796/1996, art. 18, I, \"j\"",
    });
    const baseEntrada = custo.times(new D(1).minus(entrada.reducao_base));
    passos.push({
      chave: "base_entrada",
      descricao: "Base do ICMS na NF de compra",
      formula: "custo_avista × (1 − reducao_base)",
      valor: baseEntrada.toString(),
      fonte: "RICMS-SE, art. 674-A, § 1º (\"valor que serviu de base de cálculo\" da entrada)",
    });
    const base674 = baseEntrada.plus(freteValor).plus(ipi);
    passos.push({
      chave: "base_complementacao",
      descricao: "Base da complementação (base da entrada + frete do produto + IPI)",
      formula: "base_entrada + frete_compra + ipi",
      valor: base674.toString(),
      fonte: "RICMS-SE, art. 674-A, § 1º",
    });
    const icmsValor = D.max(0, base674.times(interna).minus(baseEntrada.times(entrada.interestadual_nf)));
    passos.push({
      chave: "icms_valor",
      descricao: "Complementação em R$ (interna sobre a base, menos o ICMS da operação interestadual)",
      formula: "max(0, base_complementacao × interna_aplicavel − base_entrada × interestadual)",
      valor: icmsValor.toString(),
      fonte: `${FONTE_DIFAL_SIMPLES}, § 2º; fórmula do DIA da SEFAZ-SE (doc 21, §4.3)`,
    });
    const fecoepValor = entrada.fecoep_isento ? new D(0) : base674.times(fecoep);
    passos.push({
      chave: "fecoep_valor",
      descricao: entrada.fecoep_isento ? "FECOEP: material escolar, não incide" : "FECOEP em R$ (DAE separado)",
      formula: entrada.fecoep_isento ? "0" : "base_complementacao × fecoep",
      valor: fecoepValor.toString(),
      fonte: entrada.fecoep_isento
        ? "RICMS-SE, art. 616-C-A, IV, \"c\"; Lei SE 4.731/2002, art. 2º-A, § 3º, II, \"c\""
        : "RICMS-SE, art. 40-D e art. 674-A, § 8º; Lei SE 4.731/2002, art. 2º-B, II",
    });
    impostoValor = icmsValor.plus(fecoepValor);
  }

  const base = custoBase.plus(impostoValor);
  const icmsFracao = custoBase.isZero() ? new D(0) : impostoValor.div(custoBase);
  passos.push({
    chave: "base_com_icms",
    descricao: "Custo base acrescido da complementação e do FECOEP",
    formula: "custo_base + icms_valor + fecoep_valor",
    valor: base.toString(),
    fonte: "RICMS-SE, art. 674-A, § 4º (não gera crédito)",
  });
  passos.push({
    chave: "icms_peso",
    descricao: "Peso do ICMS + FECOEP sobre o custo base (informativo)",
    formula: "(icms_valor + fecoep_valor) ÷ custo_base",
    valor: icmsFracao.toString(),
  });

  const markup = calcularMarkup(entrada.markup, fretePct, passos);
  return montarResultado(entrada, passos, base, icmsFracao, origem, markup);
}

/** Calcula o preço unitário de venda com memória de cálculo completa. */
export function calcularPreco(entrada: EntradaPreco): ResultadoPreco {
  switch (entrada.regra) {
    case "v1-legado":
      return calcularV1(entrada);
    case "v2":
      return calcularV2(entrada);
    case "v3":
      return calcularV3(entrada);
  }
}
